import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Container, Theme } from '@mui/material';
import Navigation from '@/components/navigation/Navigation';
import { useAuth } from '@/contexts/AuthContext';

// Dashboard-specific theme extensions
const dashboardStyles = {
  dashboard: {
    container: {
      minHeight: '100vh',
      width: '100vw',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      overflow: 'hidden'
    },
    main: {
      flexGrow: 1,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      bgcolor: (theme: Theme) => theme.palette.background.default
    },
    content: {
      mt: 4,
      mb: 8,
      px: { xs: 4, sm: 6, md: 8 },
      flex: 1,
      display: 'flex',
      flexDirection: 'column'
    },
    // Analytics-specific styles
    analytics: {
      card: {
        borderRadius: 2,
        boxShadow: (theme: Theme) => theme.shadows[2],
        bgcolor: 'background.paper',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: (theme: Theme) => theme.shadows[8]
        }
      },
      chart: {
        borderRadius: 2,
        p: 3,
        height: '100%',
        minHeight: 300
      },
      statCard: {
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2
      },
      gridContainer: {
        mt: 3,
        gap: { xs: 3, md: 4 }
      }
    },
    // Tab styles
    tabs: {
      wrapper: {
        borderBottom: 1,
        borderColor: 'divider',
        mb: 4
      },
      tab: {
        minWidth: 120,
        textTransform: 'none',
        fontWeight: 'medium'
      }
    }
  }
} as const;

interface TeacherLayoutProps {
  children: React.ReactNode;
}

const TeacherLayout: React.FC<TeacherLayoutProps> = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated || user?.role !== 'teacher') {
    return <Navigate to="/login" replace />;
  }

  return (
    <Box sx={dashboardStyles.dashboard.container}>
      <Navigation 
        variant="default"
        userRole={user?.role}
      />
      <Box
        component="main"
        sx={dashboardStyles.dashboard.main}
      >
        <Container 
          maxWidth="xl" 
          sx={dashboardStyles.dashboard.content}
        >
          {children}
        </Container>
      </Box>
    </Box>
  );
};

// Export styles for use in dashboard components
export { dashboardStyles };
export default TeacherLayout;
